import React, { useEffect, useState, useRef } from 'react';
import { HelpCircleIcon, ExternalLinkIcon } from 'lucide-react';
import { createPortal } from 'react-dom';
interface TooltipProps {
  title: string;
  description: string;
  learnMoreUrl?: string;
  learnMoreText?: string;
}
export const Tooltip: React.FC<TooltipProps> = ({
  title,
  description,
  learnMoreUrl,
  learnMoreText
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({
    top: 0,
    left: 0
  });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const tooltipWidth = 288; // w-72 = 18rem = 288px
      const tooltipHeight = 200; // approximate height
      const padding = 8;
      let top = buttonRect.bottom + padding;
      let left = buttonRect.left;
      // Check if tooltip goes off right edge
      if (left + tooltipWidth > window.innerWidth) {
        left = window.innerWidth - tooltipWidth - padding;
      }
      // Check if tooltip goes off left edge
      if (left < padding) {
        left = padding;
      }
      // Check if tooltip goes off bottom edge
      if (top + tooltipHeight > window.innerHeight) {
        // Position above the button instead
        top = buttonRect.top - tooltipHeight - padding;
      }
      // Check if tooltip goes off top edge
      if (top < padding) {
        top = padding;
      }
      setPosition({
        top,
        left
      });
    }
  }, [isOpen]);
  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsOpen(true);
  };
  const handleMouseLeave = () => {
    // Add a small delay before closing to allow cursor to move to tooltip
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 100);
  };
  const handleTooltipMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };
  const handleTooltipMouseLeave = () => {
    setIsOpen(false);
  };
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  return <>
      <button ref={buttonRef} type="button" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onClick={() => setIsOpen(!isOpen)} className="ml-1 text-gray-400 hover:text-blue-400 transition-colors inline-flex items-center" aria-label="More information" data-id="element-245">
        <HelpCircleIcon className="w-4 h-4" data-id="element-246" />
      </button>
      {isOpen && createPortal(<div ref={tooltipRef} onMouseEnter={handleTooltipMouseEnter} onMouseLeave={handleTooltipMouseLeave} className="fixed z-[9999] w-72 p-4 bg-[#1E293B] border border-blue-500 rounded-lg shadow-2xl" style={{
      top: `${position.top}px`,
      left: `${position.left}px`
    }} data-id="element-247">
            <div className="text-sm font-semibold text-blue-400 mb-2" data-id="element-248">
              {title}
            </div>
            <div className="text-xs text-gray-300 leading-relaxed mb-3" data-id="element-249">
              {description}
            </div>
            {learnMoreUrl && <a href={learnMoreUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:text-blue-300 flex items-center transition-colors hover:underline" data-id="element-250">
                {learnMoreText || 'Learn more from Brian Balfour'}
                <ExternalLinkIcon className="w-3 h-3 ml-1 flex-shrink-0" data-id="element-251" />
              </a>}
          </div>, document.body)}
    </>;
};